import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";

interface ConfirmDialogProps {
	open: boolean;
	title: string;
	message: string;
	confirmLabel?: string;
	cancelLabel?: string;
	/** 取り消せない操作は destructive (既定)。それ以外の確認は primary。 */
	confirmVariant?: "destructive" | "primary";
	/** 確定を押せなくする (確かめている途中・する事が無いなど)。 */
	confirmDisabled?: boolean;
	/** キャンセルと確定の間に置く、もう 1 つの確定 (「反映せずに停止」など)。 */
	alternative?: { label: string; onConfirm: () => Promise<void> };
	onConfirm: () => Promise<void>;
	onCancel: () => void;
	/** 文の下に添える中身。 */
	children?: ReactNode;
}

export function ConfirmDialog({
	open,
	title,
	message,
	confirmLabel = "決定",
	cancelLabel = "キャンセル",
	confirmVariant = "destructive",
	confirmDisabled = false,
	alternative,
	onConfirm,
	onCancel,
	children,
}: ConfirmDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [loading, setLoading] = useState<"confirm" | "alternative" | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
			setLoading(null);
			setError(null);
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	const run = async (kind: "confirm" | "alternative", action: () => Promise<void>) => {
		setLoading(kind);
		setError(null);
		try {
			await action();
		} catch (err) {
			setError(err instanceof Error ? err.message : "エラーが発生しました");
			setLoading(null);
		}
	};

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key !== "Escape") return;
			// 処理済みにして、ネイティブの cancel と外側のポップオーバーの Escape で二重に閉じないようにする。
			e.preventDefault();
			if (loading === null) onCancel();
		},
		[onCancel, loading],
	);

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			className="fixed inset-0 z-50 m-auto w-[360px] rounded-4 border border-line-1 bg-surface-1 p-0 shadow-popover backdrop:bg-scrim"
			data-testid="confirm-dialog"
		>
			<div className="px-5 pt-5 pb-4">
				<h3 className="text-ui font-semibold text-fg-1">{title}</h3>
				<p className="mt-2 text-caption leading-relaxed text-fg-2">{message}</p>
				{children}
				{error && (
					<p className="mt-3 text-caption" style={{ color: "var(--danger)" }}>
						{error}
					</p>
				)}
			</div>
			<div className="flex justify-end gap-2 border-t border-line-1 px-5 py-3">
				<ConfirmButton
					variant="ghost"
					size="sm"
					label={cancelLabel}
					loading={false}
					disabled={loading !== null}
					onClick={onCancel}
					data-testid="confirm-dialog-cancel"
				/>
				{alternative && (
					<ConfirmButton
						label={alternative.label}
						loading={loading === "alternative"}
						disabled={loading !== null}
						variant="secondary"
						size="sm"
						onClick={() => void run("alternative", alternative.onConfirm)}
						data-testid="confirm-dialog-alternative"
					/>
				)}
				<ConfirmButton
					label={confirmLabel}
					loading={loading === "confirm"}
					disabled={confirmDisabled || loading !== null}
					variant={confirmVariant}
					size="sm"
					onClick={() => void run("confirm", onConfirm)}
					data-testid="confirm-dialog-confirm"
				/>
			</div>
		</dialog>
	);
}
